/**
 * Full Project Configuration page — every field with per-field
 * metadata (value, source, review status, last modified, modified by,
 * confidence, lock state), inline edit, validate, save draft,
 * save-new-version and version history restore.
 *
 * Sprint P37y · Sprint B (Feb 2026).
 *
 * Backed by the unified `project_configuration` endpoints created in
 * Sprint A. The route is `/app/projects/:pid/setup/configuration`.
 */
import { useCallback, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ClockCounterClockwise, DownloadSimple, CheckCircle, Warning,
  IdentificationCard, MapPin, Package, Globe, ChartBar, Ruler, Diamond,
  User, ArrowClockwise, Files, ArrowUUpLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ConfigFieldRow from "@/pages/setup/createproject/parts/ConfigFieldRow";
import ImpactBadge from "./ImpactBadge";

const SECTIONS = [
  { key: "identity",       label: "Project Identity & Ownership",   icon: IdentificationCard },
  { key: "location",       label: "Location & Licence",              icon: MapPin },
  { key: "commodity",      label: "Commodity & Deposit Type",       icon: Package },
  { key: "crs",             label: "CRS, Datum & Coordinate System", icon: Globe },
  { key: "data",            label: "Drillhole & Exploration Data",   icon: ChartBar },
  { key: "modelling",       label: "Geological Modelling Parameters", icon: Ruler },
  { key: "classification", label: "Classification & Economics",      icon: Diamond },
  { key: "team",            label: "Team, CP & Reporting",             icon: User },
];

// Labels for the eight fixed keys shown in each section. Missing keys
// still render (using the field-name humanised) so any backend addition
// is visible immediately.
const FIELD_LABELS = {
  identity: {
    name: "Project Name", project_code: "Project Code",
    internal_project_number: "Internal Project Number",
    client: "Client", company: "Consultant Company", consultant: "Consultant",
    project_manager: "Project Manager", description: "Description",
    project_stage: "Project Stage",
  },
  location: {
    country: "Country", state: "State / Province", district: "District",
    village: "Village", locality: "Locality",
    latitude: "Latitude", longitude: "Longitude", elevation: "Elevation",
    mining_licence: "Mining Licence", prospecting_licence: "Prospecting Licence",
    exploration_licence: "Exploration Licence", lease_number: "Lease Number",
    licence_area: "Licence Area (km²)",
  },
  commodity: {
    commodity: "Primary Commodity", deposit_type: "Deposit Type",
    mineralisation_style: "Mineralisation Style",
    grade_symbol: "Grade Symbol", grade_unit: "Grade Unit",
    metal_unit: "Metal Unit", secondary_commodities: "Secondary Commodities",
  },
  crs: {
    epsg: "EPSG Code", crs_name: "CRS Name",
    utm_zone: "UTM Zone", hemisphere: "Hemisphere",
    horizontal_datum: "Horizontal Datum", vertical_datum: "Vertical Datum",
    coord_units: "Coordinate Units",
    boundary_ref: "Boundary Reference", boundary_source: "Boundary Source",
    boundary_approved: "Boundary Approved",
  },
  data: {
    collar_count: "Collars", survey_count: "Surveys", assay_count: "Assays",
    lithology_count: "Lithology Rows", density_count: "Density Rows",
    qaqc_count: "QA/QC Rows",
    drill_campaigns: "Drilling Campaigns", drill_spacing_m: "Drill Spacing (m)",
    boundary_present: "Boundary Present", dem_present: "DEM Present",
  },
  modelling: {
    density_default: "Default Bulk Density", density_source: "Density Source",
    composite_length: "Composite Length (m)",
    block_size_x: "Block Size X", block_size_y: "Block Size Y", block_size_z: "Block Size Z",
    search_radius: "Search Radius (m)",
    search_rx: "Search Rx", search_ry: "Search Ry", search_rz: "Search Rz",
    search_azimuth: "Search Azimuth", search_plunge: "Search Plunge", search_dip: "Search Dip",
    top_cut: "Top-Cut", estimation_method: "Estimation Method",
    min_samples: "Min Samples", max_samples: "Max Samples", min_holes: "Min Holes",
  },
  classification: {
    classification_method: "Classification Method",
    measured_distance: "Measured Distance (m)",
    indicated_distance: "Indicated Distance (m)",
    inferred_distance: "Inferred Distance (m)",
    cutoff_grade: "Working Cut-off",
    reporting_cutoff: "Reporting Cut-off",
    metal_price: "Metal Price",
    mining_cost: "Mining Cost", processing_cost: "Processing Cost",
    g_and_a_cost: "G&A Cost", selling_cost: "Selling Cost",
    mining_recovery: "Mining Recovery %",
    processing_recovery: "Processing Recovery %", dilution: "Dilution %",
    reporting_currency: "Reporting Currency", mining_method: "Mining Method",
  },
  team: {
    cp_name: "Competent Person Name",
    cp_registration: "CP Registration Number",
    cp_membership: "CP Institute / Membership",
    cp_independence: "CP Independence",
    cp_email: "CP Email", cp_phone: "CP Phone",
    qp_name: "Qualified Person Name", qp_registration: "QP Registration",
    reporting_standard: "Reporting Standard",
    secondary_standard: "Secondary Reporting Standard",
    report_type: "Report Type",
    effective_date: "Effective Date", data_cutoff_date: "Data Cut-off Date",
    issue_date: "Issue Date",
    distribution: "Distribution", confidentiality: "Confidentiality",
  },
};

function humanise(k) { return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()); }

const NUMERIC_KEYS = new Set([
  "latitude", "longitude", "elevation", "licence_area",
  "collar_count", "survey_count", "assay_count", "lithology_count",
  "density_count", "qaqc_count", "drill_campaigns", "drill_spacing_m",
  "density_default", "composite_length", "block_size_x", "block_size_y",
  "block_size_z", "search_radius", "search_rx", "search_ry", "search_rz",
  "search_azimuth", "search_plunge", "search_dip", "top_cut",
  "min_samples", "max_samples", "min_holes",
  "measured_distance", "indicated_distance", "inferred_distance",
  "cutoff_grade", "reporting_cutoff", "metal_price",
  "mining_cost", "processing_cost", "g_and_a_cost", "selling_cost",
  "mining_recovery", "processing_recovery", "dilution",
  "utm_zone", "epsg",
]);
const SELECT_OPTIONS = {
  hemisphere: ["N", "S"],
  boundary_approved: ["true", "false"],
  estimation_method: ["OrdinaryKriging", "InverseDistance", "NearestNeighbour", "IndicatorKriging", "SimpleKriging"],
  classification_method: ["distance", "kriging_variance", "block_confidence", "manual"],
  mining_method: ["open_pit", "underground", "combined", "in_situ_leach", "placer"],
  cp_independence: ["independent", "non_independent"],
  project_stage: ["early_exploration", "advanced_exploration", "mre", "reserve", "mine_planning", "feasibility", "cpr_update"],
};

export default function ProjectConfigurationPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);
  const [sessionChanges, setSessionChanges] = useState([]);   // dotted keys touched this session

  const cfgQ = useQuery({
    queryKey: ["config", pid],
    queryFn: () => api.get(`/projects/${pid}/configuration`).then((r) => r.data),
  });
  const historyQ = useQuery({
    queryKey: ["config-history", pid],
    queryFn: () => api.get(`/projects/${pid}/configuration/history?limit=100`).then((r) => r.data),
    enabled: showHistory,
  });

  const patchMut = useMutation({
    mutationFn: ({ patches, summary }) =>
      api.patch(`/projects/${pid}/configuration`, { patches, summary }).then((r) => r.data),
    onSuccess: () => {
      cfgQ.refetch();
      qc.invalidateQueries({ queryKey: ["config-history", pid] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const onPatchField = useCallback((section, field, patch) => {
    const key = `${section}.${field}`;
    setSessionChanges((prev) => prev.includes(key) ? prev : [...prev, key]);
    patchMut.mutate({ patches: { [key]: patch },
                         summary: `Edited ${key}` });
  }, [patchMut]);

  const downloadConfig = () => {
    const cfg = cfgQ.data?.configuration;
    if (!cfg) return;
    const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${cfgQ.data.project?.name || pid}_configuration_v${cfg.version_number || 1}.json`;
    a.click();
  };

  const validate = async () => {
    // Basic client-side validation — required fields per section.
    const cfg = cfgQ.data?.configuration || {};
    const missing = [];
    if (!(cfg.identity?.name?.value)) missing.push("Project name");
    if (!(cfg.location?.country?.value)) missing.push("Country");
    if (!(cfg.commodity?.commodity?.value)) missing.push("Commodity");
    if (!(cfg.team?.reporting_standard?.value)) missing.push("Reporting standard");
    if (missing.length) {
      toast.error(`Missing required: ${missing.join(", ")}`);
    } else {
      toast.success("Configuration is complete for publication.");
    }
  };

  if (cfgQ.isLoading) {
    return <div className="flex h-full items-center justify-center text-slate-500 text-sm">Loading configuration…</div>;
  }
  if (cfgQ.error) {
    return <div className="flex h-full items-center justify-center text-rose-700 text-sm">Failed to load configuration.</div>;
  }

  const cfg = cfgQ.data?.configuration || {};
  const project = cfgQ.data?.project || {};

  return (
    <div className="h-full flex flex-col bg-slate-50 min-h-0" data-testid="project-config-page">
      {/* ── FIXED header ── */}
      <header className="bg-white border-b border-slate-200 shrink-0" data-testid="config-fixed-top">
        <div className="px-6 py-3 flex items-center gap-4">
          <button onClick={() => nav(`/app/projects/${pid}/setup`)}
                     className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 hover:text-brand-blue"
                     data-testid="btn-back-to-setup">
            <ArrowLeft size={12} /> Command Center
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
              Project Setup & Resource Estimation · Configuration
            </div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none truncate">
              {project.name || "Project"} — Full Configuration
            </h1>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Schema {cfg.schema_version || "v1"} · Version {cfg.version_number || 1} ·
              {cfg.updated_at ? ` Updated ${new Date(cfg.updated_at).toLocaleString()}` : " Not updated"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={validate} data-testid="btn-validate-config"
                       className="gap-1 text-[11.5px]">
              <CheckCircle size={12} /> Validate
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)} data-testid="btn-toggle-history"
                       className="gap-1 text-[11.5px]">
              <ClockCounterClockwise size={12} /> {showHistory ? "Hide History" : "Version History"}
            </Button>
            <Button variant="outline" size="sm" onClick={downloadConfig} data-testid="btn-download-config"
                       className="gap-1 text-[11.5px]">
              <DownloadSimple size={12} /> Download JSON
            </Button>
          </div>
        </div>
      </header>

      {/* ── SCROLLABLE middle: sticky section nav + main scroll ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden" data-testid="config-scroll-region">
        {/* Section nav — independent scroll */}
        <SectionNavRail sections={SECTIONS} cfg={cfg} />

        {/* Main content — independent scroll */}
        <div className="flex-1 min-w-0 overflow-y-auto" data-testid="config-main-scroll">
          <div className={`px-6 py-4 max-w-7xl mx-auto grid gap-4 ${showHistory ? "grid-cols-1 lg:grid-cols-[1fr_320px]" : "grid-cols-1"}`}>
            <main className="space-y-4 min-w-0">
              {sessionChanges.length > 0 && (
                <ImpactBadge pid={pid} changedFields={sessionChanges}
                                  onConfirm={() => {
                                    toast.info("Regeneration is opt-in: use the GIS Studio / CPR pages when ready.");
                                    setSessionChanges([]);
                                  }} />
              )}
              {SECTIONS.map((s) => (
            <SectionCard key={s.key} section={s.key} label={s.label} Icon={s.icon}
                              slots={cfg[s.key] || {}}
                              onPatch={(field, patch) => onPatchField(s.key, field, patch)} />
          ))}
        </main>

        {/* History side panel */}
        {showHistory && (
          <aside className="rounded-lg border border-slate-200 bg-white p-4 max-h-[80vh] overflow-y-auto sticky top-24"
                     data-testid="history-panel">
            <div className="flex items-center gap-2 mb-3">
              <ClockCounterClockwise size={14} className="text-brand-blue" />
              <h2 className="font-heading font-bold text-[13px] text-slate-900">Version History</h2>
              <button type="button" onClick={() => historyQ.refetch()}
                         data-testid="btn-refresh-history"
                         className="ml-auto text-[10.5px] text-slate-500 hover:text-brand-blue">
                <ArrowClockwise size={11} className={historyQ.isFetching ? "animate-spin" : ""} />
              </button>
            </div>
            {(!historyQ.data?.entries || historyQ.data.entries.length === 0) && (
              <div className="text-[11px] text-slate-500 italic">No changes yet — every edit will appear here.</div>
            )}
            <ol className="space-y-2">
              {(historyQ.data?.entries || []).map((h, i) => (
                <li key={i} data-testid={`history-entry-${i}`}
                     className="text-[11px] border border-slate-100 rounded-md p-2 bg-slate-50/40">
                  <div className="flex items-center gap-2">
                    <div className="text-brand-blue font-mono font-bold">v{h.version_number}</div>
                    <div className="text-slate-500 text-[10px]">{new Date(h.at).toLocaleString()}</div>
                    {h.kind === "ai_suggestions" && (
                      <span className="ml-auto rounded-sm border border-brand-blue/30 bg-brand-blue/5 text-brand-blue px-1 text-[8.5px] uppercase tracking-widest">AI</span>
                    )}
                  </div>
                  {h.change_summary && (
                    <div className="mt-1 text-slate-700 italic">{h.change_summary}</div>
                  )}
                  <div className="mt-1 text-[10.5px] text-slate-500 line-clamp-2">
                    {(h.changed_fields || []).slice(0, 6).join(", ")}
                    {(h.changed_fields || []).length > 6 && ` + ${(h.changed_fields || []).length - 6} more`}
                  </div>
                </li>
              ))}
            </ol>
          </aside>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sticky section navigation rail — jumps to a target `<section id="section-key">`
 * inside the main scroll container and shows completion stats.
 */
function SectionNavRail({ sections, cfg }) {
  const [active, setActive] = useState(sections[0]?.key);

  const goTo = (key) => {
    setActive(key);
    const el = document.getElementById(`section-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const stats = (s) => {
    const slots = cfg[s.key] || {};
    let total = 0, filled = 0, warnings = 0;
    for (const k of Object.keys(slots)) {
      total += 1;
      const slot = slots[k] || {};
      if (slot.value != null && slot.value !== "") filled += 1;
      if (slot.review_status === "Technical Review Required" ||
          slot.review_status === "CP/QP Review Required") warnings += 1;
    }
    return { total, filled, warnings, pct: total ? Math.round(filled / total * 100) : 0 };
  };

  return (
    <nav className="hidden lg:block w-56 shrink-0 h-full overflow-y-auto border-r border-slate-200 bg-white"
             data-testid="config-section-nav">
      <div className="text-[9.5px] uppercase tracking-[0.24em] text-slate-500 px-4 pt-4 pb-2 sticky top-0 bg-white">
        Sections
      </div>
      <ul className="p-2 space-y-0.5">
        {sections.map((s) => {
          const st = stats(s);
          const isActive = active === s.key;
          const done = st.pct >= 80;
          return (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => goTo(s.key)}
                data-testid={`nav-section-${s.key}`}
                className={`w-full text-left rounded-md px-2 py-1.5 flex items-center gap-2 text-[11.5px] transition
                              ${isActive ? "bg-brand-blue/10 text-brand-blue"
                                            : "text-slate-700 hover:bg-slate-50"}`}
              >
                <s.icon size={11} weight="duotone"
                             className={isActive ? "text-brand-blue" : "text-slate-500"} />
                <span className="flex-1 truncate">{s.label}</span>
                <span className={`text-[9.5px] font-mono ${done ? "text-emerald-600" : "text-slate-400"}`}>
                  {st.pct}%
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SectionCard({ section, label, Icon, slots, onPatch }) {
  const [expanded, setExpanded] = useState(true);
  // union of predefined label keys + any keys actually present in slots
  const keys = useMemo(() => {
    const s = new Set([...Object.keys(FIELD_LABELS[section] || {}), ...Object.keys(slots || {})]);
    return Array.from(s);
  }, [section, slots]);
  const filled = useMemo(() => keys.filter((k) => {
    const v = (slots[k] || {}).value;
    return v !== null && v !== undefined && v !== "";
  }).length, [keys, slots]);
  const pct = keys.length ? Math.round(filled / keys.length * 100) : 0;
  return (
    <section id={`section-${section}`} className="rounded-lg bg-white border border-slate-200 scroll-mt-6"
                data-testid={`section-${section}`}>
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 sticky top-0 z-10 bg-white/95 backdrop-blur">
        <Icon size={14} className="text-brand-blue" weight="duotone" />
        <h2 className="font-heading font-bold text-[13.5px] text-slate-900 flex-1">{label}</h2>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <span className="font-mono">{filled}/{keys.length}</span>
          <span className="w-16 h-1 rounded-full bg-slate-100 overflow-hidden">
            <span className={`block h-full ${pct >= 80 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-400"}`}
                     style={{ width: `${pct}%` }} />
          </span>
          <span className="font-mono">{pct}%</span>
        </div>
        <button type="button" onClick={() => setExpanded((v) => !v)}
                    data-testid={`toggle-section-${section}`}
                    className="text-[10.5px] text-slate-500 hover:text-brand-blue ml-2">
          {expanded ? "Collapse" : "Expand"}
        </button>
      </header>
      {expanded && (
        <div className="divide-y divide-slate-100">
          {keys.map((k) => {
            const slot = slots[k] || {};
            const isNumeric = NUMERIC_KEYS.has(k);
            const opts = SELECT_OPTIONS[k];
            const type = opts ? "select" : (isNumeric ? "number" : "text");
            return (
              <ConfigFieldRow
                key={k}
                section={section}
                field={k}
                label={(FIELD_LABELS[section] || {})[k] || humanise(k)}
                type={type}
                options={opts || []}
                slot={slot}
                onChange={(v) => onPatch(k, { value: v, source: "manual", review_status: "Manually Edited" })}
                onResetToAI={(ai) => onPatch(k, { value: ai, source: "ai_suggested", review_status: "AI Suggested" })}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
